import { useEffect, useState } from 'preact/hooks'
import { useView } from '../app/use-view.ts'

/** iOS's automatic appearance, by the game clock: dark from 20:00 to 07:00. */
const DARK_FROM = 20 * 60
const LIGHT_FROM = 7 * 60

export type OsAppearance = 'light' | 'dark'

export function appearanceAt(minutes: number): OsAppearance {
  return minutes >= DARK_FROM || minutes < LIGHT_FROM ? 'dark' : 'light'
}

/** 1307 → `'21:47'`. */
export function formatOsTime(minutes: number): string {
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`
}

function deviceMinutes(): number {
  const now = new Date()
  return now.getHours() * 60 + now.getMinutes()
}

/** The OS's time in minutes since midnight: the game's clock, or the device's without a sky. */
export function useOsClock(): number {
  const game = useView((view) => view.os.time)
  const [device, setDevice] = useState(deviceMinutes)
  useEffect(() => {
    if (game !== null) return
    const tick = (): void => {
      setDevice(deviceMinutes())
    }
    tick()
    const timer = setInterval(tick, 10_000)
    return () => {
      clearInterval(timer)
    }
  }, [game])
  return game ?? device
}

/** Game minutes in a day. */
const DAY_MINUTES = 24 * 60

/** Absolute game minutes (`clock.now`) from `view.os`'s day and time; null without a sky. */
export function gameNow(day: number | null, minutes: number | null): number | null {
  return day === null || minutes === null ? null : (day - 1) * DAY_MINUTES + minutes
}

/** The game day (from 1) absolute game minutes fall on. */
export function gameDay(at: number): number {
  return Math.floor(at / DAY_MINUTES) + 1
}

/** Absolute game minutes → minutes since that day's midnight. */
export function timeOfDay(at: number): number {
  return at % DAY_MINUTES
}
